'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { Icon } from '@/ui/icons/icon/Icon'
import { Notification } from '@/ui/notification/Notification'
import { cva } from 'class-variance-authority'
import { useContext, type ReactNode } from 'react'
import { Button, DisclosureStateContext, Heading } from 'react-aria-components'
import { useAccordion } from '../_context/AccordionContext'
import '../Accordion.css'

export type AccordionHeaderProps = UiComponentProps & {
  title: ReactNode
} & (
    | {
        preTitle?: never
        picture?: never
        notificationCount?: never
        compact: true
      }
    | {
        preTitle?: ReactNode
        picture?: ReactNode
        notificationCount?: number
        compact?: false
      }
  )

const headerAccentTextClass = twMerge(
  'break-words',
  'text-[--accordion-fg-default] transition-colors',
  'group-data-[hovered]:text-[--accordion-fg-hover] group-data-[pressed]:text-[--accordion-fg-pressed]',
)

const chevronStyles = cva('ml-auto size-4 shrink-0 transition-[transform,color] duration-200', {
  variants: {
    isExpanded: {
      true: 'rotate-90',
    },
    isDisabled: {
      true: 'text-neutral-300 dark:text-neutral-600 forced-colors:text-[GrayText]',
      false:
        'text-[--accordion-fg-default] group-data-[hovered]:text-[--accordion-fg-hover] group-data-[pressed]:text-[--accordion-fg-pressed]',
    },
  },
  defaultVariants: {
    isExpanded: false,
    isDisabled: false,
  },
})

function AccordionItemHeaderLabel({
  preTitle,
  title,
  notificationCount,
  compact = false,
}: AccordionHeaderProps) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="flex flex-col items-start">
        <span className={headerAccentTextClass}>{preTitle}</span>
        <span className="inline-block max-w-full">
          <span className={twMerge('font-bold', !compact && 'uppercase', headerAccentTextClass)}>
            {title}
          </span>
        </span>
      </div>
      {notificationCount && (
        <Notification number={notificationCount} aria-label="Notification count" />
      )}
    </div>
  )
}

export function AccordionItemHeader({
  id,
  picture,
  className,
  'aria-label': ariaLabel,
  ...props
}: AccordionHeaderProps) {
  const ctx = useContext(DisclosureStateContext)
  const accordionItemId = useAccordion()
  const triggerId = id ?? accordionItemId

  return (
    <Heading className="font-bold">
      <Button
        slot="trigger"
        data-testid={triggerId !== undefined ? `accordion-item/trigger-${triggerId}` : undefined}
        aria-label={ariaLabel}
        className={twMerge(
          'group flex min-w-0 items-center p-2 text-left font-medium',
          !props.compact ? 'w-full' : 'gap-1',
          className,
        )}
      >
        {({ isDisabled }) => (
          <>
            {picture && (
              <div
                className="relative flex size-5 shrink-0 overflow-hidden [&_img]:size-full [&_img]:object-cover [&_picture]:size-full"
                data-testid={`accordion-item/picture-${triggerId}`}
              >
                {picture}
              </div>
            )}
            <AccordionItemHeaderLabel {...props} />
            <Icon.ChevronRight
              aria-hidden
              size={props.compact ? 'sm' : 'md'}
              className={chevronStyles({ isExpanded: ctx?.isExpanded, isDisabled })}
            />
          </>
        )}
      </Button>
    </Heading>
  )
}
